'use client';

import React, { useRef } from "react";
import RoundedIcon from "@/components/ui/RoundedIcon/RoundedIcon";
import { Icon } from "@phosphor-icons/react";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitType from 'split-type';
import { useGSAP } from "@gsap/react";
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export interface Facility {
  label: string;
  icon: Icon;
}

interface FacilitiesSectionProps {
  language?: LanguageType
  heading: string
  subheading: string
  facilities: Facility[]
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};


const FacilitiesSection: React.FC<FacilitiesSectionProps> = ({
  language = "english",
  heading,
  subheading,
  facilities,
}) => {
  const isLg = useIsLg();
  const isTb = useIsTb();
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLParagraphElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const subheadingRef = useRef<HTMLParagraphElement>(null);
  const iconsContainerRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const section = sectionRef.current;
    const headingElement = headingRef.current;
    const logo = logoRef.current;
    const subheading = subheadingRef.current;
    const iconWrappers = iconsContainerRef.current?.children;
    const iconContainer = iconsContainerRef.current;

    if (!section || !headingElement || !logo || !subheading || !iconWrappers || !iconContainer) return;

    if (isLg) {
      // DESKTOP LOGIC
      const splitHeading = new SplitType(headingElement, { types: 'words' });

      // Set initial states
      gsap.set([logo, subheading], { autoAlpha: 0 });
      gsap.set(Array.from(iconWrappers), {
        autoAlpha: 0,
        filter: 'blur(30px)',
        y: +100,
        force3D: true,
      });
      gsap.set(splitHeading.words, { autoAlpha: 0 });

      // Create a separate timeline for secondary animations (paused initially)
      const secondaryTl = gsap.timeline({ paused: true });

      // Create a scrubbed timeline for heading animation
      const scrubTl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          end: "center center+=10%",
          scrub: true,
          // markers: true,
        },
        onComplete: () => {
          secondaryTl.play();
          scrubTl.kill();
        }
      });

      // Heading animation in scrubbed timeline
      scrubTl.to(splitHeading.words, {
        autoAlpha: 1,
        stagger: 0.15,
        ease: "none",
        force3D: true,
      });

      // Secondary animations in separate timeline
      secondaryTl
        .to([logo, subheading], {
          autoAlpha: 1,
          stagger: 0.2,
          duration: 0.7,
          ease: "power2.out",
          force3D: true,
        })
      // Animate each icon individually when it comes into view
      Array.from(iconWrappers).forEach((iconWrapper) => {
        gsap.to(iconWrapper, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          y: 0,
          duration: 1,
          ease: "power2.out",
          force3D: true,
          scrollTrigger: {
            trigger: iconWrapper,
            start: "top 85%",
            toggleActions: "play none none none",
            // markers: true,
          }
        });
      });

    } else {
      // MOBILE LOGIC

      // Set initial states
      gsap.set([logo, subheading], { autoAlpha: 0 });
      gsap.set(Array.from(iconWrappers), {
        autoAlpha: 0,
        filter: 'blur(30px)',
        y: +50,
        force3D: true,
      });
      gsap.set(headingElement, { autoAlpha: 0 });

      // Heading animation (non-scrubbed)
      gsap.to(headingElement, {
        autoAlpha: 1,
        duration: 1,
        ease: "power2.out",
        force3D: true,
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none none",
          // markers: true,
        }
      });

      // Logo and subheading animations
      gsap.to([logo, subheading], {
        autoAlpha: 1,
        stagger: 0.2,
        duration: 0.7,
        ease: "power2.out",
        scrollTrigger: {
          trigger: logo,
          start: "top 85%",
          toggleActions: "play none none none",
          // markers: true,
        }
      });

      // Animate each icon individually when it comes into view
      Array.from(iconWrappers).forEach((iconWrapper) => {
        gsap.to(iconWrapper, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          y: 0,
          duration: 1,
          ease: "power2.out",
          force3D: true,
          scrollTrigger: {
            trigger: iconWrapper,
            start: "top 80%",
            toggleActions: "play none none none",
            // markers: true,
          }
        });
      });
    }

  }, { dependencies: [heading, isLg], revertOnUpdate: true });

  return (
    // section has custom padding!
    <section
      ref={sectionRef}
      className="
      w-full
      padding-main-x-mobile
      padding-main-x-desktop
      pt-[2rem]
      padding-main-t-desktop
      flex items-center justify-center
      ">
      <div className="w-full container-small">
        <div className="flex flex-col gap-[3.5rem] lg:gap-[6rem]">

          <p
            ref={headingRef}
            style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            className={`
              w-full
              text-center
              text-text-primary
              text-52-to-30--neue-june
          `}>
            {heading}
          </p>

          <div className="
            w-full
            flex flex-col
          items-center justify-center
            gap-[2.5rem] lg:gap-[4.5rem]
            ">
            <div className="
              w-full
              flex flex-col
              gap-[1.25rem] lg:gap-[1.5rem]
              justify-center items-center
              ">
              <img
                ref={logoRef}
                src="/Logomark-None.svg"
                alt="Support lockup with no registration"
                className="h-[40px] lg:h-[45px]"
              />
              <p
                ref={subheadingRef}
                style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                className={`
                  col-span-4 lg:col-span-12
                  text-center
                  text-text-primary
                  text-36-to-24--neue-june
                `}>
                {subheading}
              </p>
            </div>
            <div
              ref={iconsContainerRef}
              className="
              w-full
              tb:max-w-full
              grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] tb:grid-cols-[repeat(auto-fit,minmax(11.6875rem,1fr))]
              justify-items-center
              content-center
              justify-center
              gap-x-[0.75rem] lg:gap-x-[1rem]
              gap-y-[2rem] lg:gap-y-[3.5rem]
              ">
              {facilities.map((facility, index) => {
                const IconComponent = facility.icon;
                return (
                  <div key={index} className="
                      flex-grow w-full min-w-[8.5rem] tb:min-w-[11.6875rem]
                      flex flex-col gap-[1rem]
                      items-center
                      ">
                    <RoundedIcon size={isTb ? 64 : 48}
                      className="rounded-full">
                      <IconComponent className="text-graphics-primary" />
                    </RoundedIcon>
                    <p className={`
                      text-text-quaternary
                      text-20--alexandria
                      text-center
                    `}>
                      {facility.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section >
  );
}


export default FacilitiesSection;
